<template>
	<div>
		<form @submit.prevent="submitQuery" class="mb-5">
			<div>Accession</div>
			<div class="mb-3">
				<input
					type="text"
					class="input input-bordered"
					name="study-accession"
					v-model="accessionNumber"
					autocomplete="off"
					data-lpignore="true"
				/>
			</div>
			<div>
				<button class="btn btn-primary mr-2" type="submit">Find</button>
				<button class="btn" @click.prevent="accessionNumber = ''">Clear</button>
				<div v-if="loading">loading...</div>
			</div>
		</form>
		<div class="shadow-inner bg-base-200 p-2" v-if="queryResults.length > 0">
			<div class="text-lg mb-3">
				Finding studies for patient: {{ patientName }}
			</div>
			<StudyListSelect
				:table-data="queryResults"
				selectable
				:current-accession="accessionNumber"
				@selectionUpdated="updateSelection"
			></StudyListSelect>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import StudyListSelect from "./StudyListSelect.vue";
import { getClients } from "./dicomweb-client";
import { QIDOResult, ResultStudy } from "./types";
import invariant from "tiny-invariant";
import { addLog } from "../../logger";

const emit = defineEmits(["update"]);

const searchParams = new URLSearchParams(window.location.search);
const accessionNumber = ref<string>(searchParams.get("accession") || "");
const queryResults = ref<ResultStudy[]>([]);
const loading = ref(false);
const { qidoClient } = getClients();

const props = defineProps<{ urlParams?: { [key: string]: string } }>();

onMounted(() => {
	// auto-query if one of the query fields is preset
	if (accessionNumber.value) {
		submitQuery();
	}
});

const patientName = computed(() => {
	if (queryResults.value.length > 0) {
		return queryResults.value[0]["00100010"]?.Value[0].Alphabetic;
	}
	return "";
});

async function submitQuery() {
	// don't allow blank query
	if (!accessionNumber.value) return;

	loading.value = true;

	try {
		// Special delimiter for a testing accession which will load mock data
		if (accessionNumber.value.substring(0, 2) === "//") {
			const accession = accessionNumber.value.substring(2);
			if (accession === "test") {
				const results = (await import("./mock-study-response"))
					.default as unknown as QIDOResult[];
				queryResults.value = results;
				return;
			}
		}

		// query by accession to get patient ID
		const results: QIDOResult[] = await qidoClient.searchForStudies({
			queryParams: {
				AccessionNumber: accessionNumber.value,
				includefield: "StudyDescription",
				...props.urlParams,
			},
		});

		console.log("query results:", results);

		if (results.length > 0) {
			const patientID = results[0]?.["00100020"]?.Value?.[0];
			invariant(
				patientID,
				`No patient ID for accession ${accessionNumber.value}`
			);

			// Get all studies for patient by ID
			let patientResults: ResultStudy[] = await qidoClient.searchForStudies({
				queryParams: {
					PatientID: patientID,
					limit: 500,
					includefield: "StudyDescription",
					...props.urlParams,
				},
			});

			patientResults = patientResults.filter((result) => {
				return typeof result["0020000D"]?.Value?.[0] === "string";
			});

			// Sort by date ascending
			patientResults.sort((a, b) => {
				const dateA = parseInt(a["00080020"]?.Value?.[0].toString() || "", 10);
				const dateB = parseInt(b["00080020"]?.Value?.[0].toString() || "", 10);
				if (!dateA || !dateB) return 0;
				return dateB - dateA;
			});

			// Mark study selected if accession matches initial query
			const matchedStudy = patientResults.find(
				(s) => s["00080050"]?.Value?.[0].trim() === accessionNumber.value.trim()
			);
			if (matchedStudy) matchedStudy.selected = true;
			queryResults.value = patientResults;
		} else {
			queryResults.value = [];
		}
	} catch (err) {
		addLog("error", (err as any).message || err);
		console.error(err);
	}
	loading.value = false;
}

function updateSelection() {
	const studies = queryResults.value
		.filter((s) => s?.series)
		.map((study) => {
			const series = study.series!.filter((s) => s.selected);
			return Object.assign({}, study, { series });
		});
	emit("update", studies);
}
</script>

<style scoped>
form {
	/* margin-bottom: 20px; */
}
</style>
